import { useTranslation } from 'react-i18next'

interface TimelineEvent {
  year: string
  key: string
}

const timelineEvents: TimelineEvent[] = [
  { year: '1850', key: '1850' },
  { year: '1900', key: '1900' },
  { year: '1950', key: '1950' },
  { year: '1970', key: '1970' },
  { year: '1975', key: '1975' },
  { year: '2000', key: '2000' },
  { year: '2003', key: '2003' },
  { year: '2011', key: '2011' },
  { year: '2015', key: '2015' },
  { year: '2023', key: '2023' },
]

function getTimelineImage(key: string): string {
  const imageMap: Record<string, string> = {
    '1850': '/images/about/mondo_fiorucci_1850_DESKTOP.jpg',
    '1900': '/images/about/mondo_fiorucci_1900_DESKTOP.jpg',
    '1950': '/images/about/mondo_fiorucci_1950_DESKTOP.jpg',
    '1970': '/images/about/mondo_fiorucci_1970_DESKTOP.jpg',
    '1975': '/images/about/mondo_fiorucci_1975_DESKTOP.jpg',
    '2000': '/images/about/mondo_fiorucci_2000_DESKTOP.jpg',
    '2003': '/images/about/mondo_fiorucci_2003_DESKTOP.jpg',
    '2011': '/images/Vladimir_Dukcevich_AD_CEO_Cesare_Fiorucci_SPA-news-desk.jpg',
    '2015': '/images/about/chisiamo-2011-2015.jpg',
    '2023': '/images/about/2023-chi-siamo.jpg',
  }
  return imageMap[key] || '/images/about/mondo_fiorucci_1850_DESKTOP.jpg'
}

export function Timeline() {
  const { t } = useTranslation('common')

  return (
    <section className="py-16 md:py-24 bg-white overflow-hidden relative">
      <div className="container-fiorucci mb-12 md:mb-20">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-[#DF631E] text-sm font-semibold tracking-widest uppercase mb-4 block">
            Timeline
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4E2118] mb-4">
            {t('about.timeline.title')}
          </h2>
          <p className="text-[#4E2118]/70 text-lg">
            {t('about.timeline.subtitle')}
          </p>
        </div>
      </div>

      <div className="container-fiorucci">
        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute left-6 md:left-1/2 top-4 bottom-4 w-0.5 bg-[#DF631E]/20 -translate-x-1/2" />

          <div className="space-y-16 md:space-y-24">
            {timelineEvents.map((event, index) => {
              const isLeft = index % 2 === 0
              const yearLabel = event.year === '2023' ? t('about.timeline.milestones.2023.year') : event.year

              return (
                <div
                  key={event.key}
                  className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-0 items-center"
                >
                  {/* Year badge on the line */}
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
                    <div className="w-12 h-12 rounded-full bg-[#DF631E] text-white flex items-center justify-center font-bold text-sm shadow-lg">
                      {yearLabel}
                    </div>
                  </div>

                  {/* Text content */}
                  <div
                    className={`
                      pl-16 md:pl-0 md:pr-16
                      ${isLeft ? 'md:text-right' : 'md:order-2 md:pl-16 md:pr-0 md:text-left'}
                    `}
                  >
                    <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#4E2118] mb-3">
                      {t(`about.timeline.milestones.${event.key}.title`)}
                    </h3>
                    <p className="text-[#4E2118]/70 text-lg leading-relaxed">
                      {t(`about.timeline.milestones.${event.key}.description`)}
                    </p>
                  </div>

                  {/* Image - full, not cropped */}
                  <div
                    className={`
                      pl-16 md:pl-0
                      ${isLeft ? 'md:pl-16' : 'md:order-1 md:pr-16'}
                    `}
                  >
                    <div className="rounded-2xl overflow-hidden shadow-2xl bg-[#FDF8F3]">
                      <img
                        src={getTimelineImage(event.key)}
                        alt={t(`about.timeline.milestones.${event.key}.imageAlt`)}
                        className="w-full h-auto max-h-[350px] md:max-h-[450px] object-contain mx-auto"
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
